import { useState } from 'react';

import {
  savedViewsApi,
  segmentsApi,
  type ConditionTree,
  type CreateSegmentRequest,
  type Segment,
} from '@/api/queries/workspace';
import {
  Button,
  ConditionBuilder,
  Field,
  Icon,
  IconButton,
  Input,
  ModalRoot,
  Select,
  Textarea,
} from '@/components/ui';
import { cn } from '@/utils/cn';

import { CONTACT_SEARCH_FIELDS, useConditionValueSuggestions } from '@/api/queries/conditionFields';

import { parseQuery } from '../../contacts/-components/list/parseQuery';
import { SEGMENT_COLOR_PRESETS } from './SegmentFields';

interface SegmentBuilderProps {
  open: boolean;
  onClose: () => void;
  /** Pre-fill from an existing segment (edit mode). */
  initial?: Segment;
}

export function SegmentBuilder({ open, onClose, initial }: SegmentBuilderProps) {
  const create = segmentsApi.useCreate();
  const update = segmentsApi.useUpdate();
  const { data: savedViews = [] } = savedViewsApi.useList();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState(SEGMENT_COLOR_PRESETS[0]);
  const [conditions, setConditions] = useState<ConditionTree>({
    conjunction: 'and',
    conditions: [],
  });
  const [error, setError] = useState<string | null>(null);

  // Populate the form whenever the modal transitions to open (create or edit)
  // — adjusted during render instead of in a setState-in-effect.
  const [wasOpen, setWasOpen] = useState(false);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setName(initial?.name ?? '');
      setDescription(initial?.description ?? '');
      setColor(initial?.color ?? SEGMENT_COLOR_PRESETS[0]);
      setConditions(initial?.conditions ?? { conjunction: 'and', conditions: [] });
      setError(null);
    }
  }

  // One-time copy: pull a saved view's stored power-query (filters.q) and parse it
  // into the segment's condition tree. The conditions stay fully editable after.
  const seedFromView = (viewId: string) => {
    const view = savedViews.find((v) => v.id === viewId);
    if (!view) return;
    const rawQuery = typeof view.filters?.q === 'string' ? view.filters.q : '';
    setConditions(parseQuery(rawQuery, CONTACT_SEARCH_FIELDS));
  };

  const submit = async () => {
    setError(null);
    if (!name.trim()) {
      setError('Segment name is required');
      return;
    }
    const body: CreateSegmentRequest = {
      name: name.trim(),
      description: description.trim() || undefined,
      color,
      conditions,
    };
    try {
      if (initial) {
        await update.mutateAsync({ id: initial.id, body });
      } else {
        await create.mutateAsync(body);
      }
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save segment');
    }
  };

  const isPending = create.isPending || update.isPending;

  return (
    <ModalRoot open={open} onClose={onClose} size="lg" closeOnScrim>
      <div className="ds-modal-head">
        <h2>{initial ? 'Edit segment' : 'New segment'}</h2>
        <IconButton icon="x" label="Close" className="ds-modal-close" onClick={onClose} />
      </div>
      <div className="ds-modal-body">
        <div className="ds-form-row">
          <div className="ds-form-grid-2">
            <Field label="Segment name" required>
              <Input
                autoFocus
                value={name}
                placeholder="VIP doctors, Active students…"
                onChange={(e) => setName(e.target.value)}
              />
            </Field>
            <Field label="Color">
              <div className="color-picker">
                {SEGMENT_COLOR_PRESETS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    aria-label={`Color ${c}`}
                    className={cn('swatch', c === color && 'active')}
                    style={{ background: c }}
                    onClick={() => setColor(c)}
                  />
                ))}
              </div>
            </Field>
          </div>

          <Field label="Description" hint="Shown on segment cards and in campaign target pickers">
            <Textarea
              value={description}
              placeholder="What does this segment represent?"
              onChange={(e) => setDescription(e.target.value)}
            />
          </Field>

          {savedViews.length > 0 && (
            <Field
              label="Start from existing filter"
              hint="Copies a saved view's conditions in — edit them freely afterwards"
            >
              <Select
                value=""
                placeholder="Select a saved view…"
                options={savedViews.map((v) => ({
                  value: v.id,
                  label: v.scope === 'workspace' ? `${v.name} (global)` : v.name,
                }))}
                onChange={(e) => {
                  if (e.target.value) seedFromView(e.target.value);
                }}
              />
            </Field>
          )}

          <Field label="Conditions">
            <ConditionBuilder
              value={conditions}
              onChange={setConditions}
              fields={CONTACT_SEARCH_FIELDS}
              useValueSuggestions={useConditionValueSuggestions}
            />
          </Field>

          {error && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 12px',
                background: 'var(--red-soft)',
                color: 'var(--red)',
                borderRadius: 'var(--radius-sm)',
                fontSize: 12.5,
              }}
            >
              <Icon name="alert" size={14} /> {error}
            </div>
          )}
        </div>
      </div>
      <div className="ds-modal-foot">
        <span style={{ flex: 1 }} />
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          loading={isPending}
          leftIcon={!isPending ? <Icon name="check" size={13} /> : undefined}
          onClick={() => void submit()}
        >
          {initial ? 'Save segment' : 'Create segment'}
        </Button>
      </div>
    </ModalRoot>
  );
}
